import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import useActivityInput from 'components/shared/activityInput/useActivityInput';
import Alert from 'components/shared/Alert';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import InputGroup from 'components/shared/form/InputGroup';
import StandardModal, { Actions, PatientName } from 'components/shared/StandardModal';
import { useTranslation } from 'context/translations';
import { simpleDate } from 'lib/date';
import { ActivityType } from 'models/Activity';
import Episode from 'models/Episode';
import LocationEpisode from 'models/LocationEpisode';
import { QuestionType } from 'models/QuestionTemplate';
import { activityQueryKeys, createActivity, ProgressUpdatePayload } from 'services/api/activity';
import { attachmentsQueryKeys } from 'services/api/attachments';
import { episodeQueryKeys } from 'services/api/episode';
import { progressChartQueryKeys } from 'services/api/locationEpisode/progressChart';
import { locationEpisodeQueryKeys } from 'services/api/locationEpisodes';
import { useToastActions } from 'stores/toastStore';

import QuestionList, { AnswerType, QuestionListRefType } from './QuestionList/QuestionList';

export type DischargeModalType = {
  setShow: (b: boolean) => void;
  patientName: string;
  locationEpisode: LocationEpisode;
  episode: Episode;
  title: string;
  invalidateData?: () => void;
  linkedRecords?: LocationEpisode[];
};

type UniqueTemplate = {
  templateId: string;
  questions: QuestionType[];
  locationEpisodeIds: string[];
  displayNames: string[];
};

function groupByTemplate(linkedRecords: LocationEpisode[], currentLocationEpisode: LocationEpisode): UniqueTemplate[] {
  const templateMap = new Map<string, UniqueTemplate>();

  // Add current location episode first (use contentKey to group by content, not ID)
  const currentContentKey = currentLocationEpisode.progressTemplate.contentKey;
  templateMap.set(currentContentKey, {
    templateId: currentContentKey,
    questions: currentLocationEpisode.progressTemplate.questions,
    locationEpisodeIds: [currentLocationEpisode.id],
    displayNames: [currentLocationEpisode.group.name],
  });

  // Add linked records
  linkedRecords.forEach((link) => {
    if (link.id === currentLocationEpisode.id) return;

    const contentKey = link.progressTemplate.contentKey;
    const existing = templateMap.get(contentKey);

    if (existing) {
      existing.locationEpisodeIds.push(link.id);
      existing.displayNames.push(link.group.name);
    } else {
      templateMap.set(contentKey, {
        templateId: contentKey,
        questions: link.progressTemplate.questions,
        locationEpisodeIds: [link.id],
        displayNames: [link.group.name],
      });
    }
  });

  return Array.from(templateMap.values());
}

export default function PatientStatusUpdateModal(props: DischargeModalType) {
  const { setShow, patientName, locationEpisode, title, invalidateData, episode, linkedRecords = [] } = props;
  const questionsRef = useRef<QuestionListRefType>(null);
  const [admittedDate, setAdmittedDate] = useState<Date | null>(null);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { note, setNote, resetNote, getSerializedNoteValues } = useActivityInput();
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  // Multi-step wizard state
  const isSharedFlow = linkedRecords.length > 0;
  const uniqueTemplates = isSharedFlow ? groupByTemplate(linkedRecords, locationEpisode) : [];
  const totalSteps = uniqueTemplates.length;
  const isSingleTemplate = totalSteps <= 1;

  const [currentStep, setCurrentStep] = useState(0);
  const [collectedAnswers, setCollectedAnswers] = useState<Map<string, AnswerType[]>>(new Map());

  const currentTemplate = isSharedFlow ? uniqueTemplates[currentStep] : null;
  const isLastStep = !isSharedFlow || currentStep === totalSteps - 1;

  const { mutate, isPending } = useMutation({
    mutationFn: createActivity,
  });

  const cancel = () => {
    questionsRef.current?.reset();
    resetNote();
    setAllQuestionsAnswered(false);
    setAdmittedDate(null);
    setCurrentStep(0);
    setCollectedAnswers(new Map());
    setShow(false);
  };

  const saveCurrentStepAnswers = () => {
    if (!currentTemplate) return;
    const answers = questionsRef.current?.getAnswers() ?? [];
    setCollectedAnswers((prev) => {
      const next = new Map(prev);
      next.set(currentTemplate.templateId, answers);
      return next;
    });
  };

  const handleNext = () => {
    saveCurrentStepAnswers();
    setCurrentStep((prev) => prev + 1);
    setAllQuestionsAnswered(false);
  };

  const handleBack = () => {
    saveCurrentStepAnswers();
    setCurrentStep((prev) => prev - 1);
  };

  const handleSuccess = () => {
    // only invalidate episode and location episode if the progress update results in the start of treatment
    if (!locationEpisode.currentRehabState.inTreatment) {
      queryClient.invalidateQueries({ queryKey: episodeQueryKeys.show({ id: locationEpisode.episodeId }) });
    }
    queryClient.invalidateQueries({ queryKey: locationEpisodeQueryKeys.show({ id: locationEpisode.id }) });
    queryClient.invalidateQueries({ queryKey: activityQueryKeys.base });
    queryClient.invalidateQueries({ queryKey: attachmentsQueryKeys.count() });
    queryClient.invalidateQueries({ queryKey: progressChartQueryKeys.show(locationEpisode.id) });
    invalidateData?.();
    cancel();
  };

  const submit = () => {
    if (isSharedFlow) {
      saveCurrentStepAnswers();

      const progressUpdates: ProgressUpdatePayload[] = [];
      uniqueTemplates.forEach((template) => {
        const answers =
          template.templateId === currentTemplate?.templateId
            ? (questionsRef.current?.getAnswers() ?? [])
            : (collectedAnswers.get(template.templateId) ?? []);

        template.locationEpisodeIds.forEach((leId) => {
          progressUpdates.push({
            locationEpisodeId: leId,
            questions: answers,
          });
        });
      });

      mutate(
        {
          ...getSerializedNoteValues(),
          type: ActivityType.PROGRESS_UPDATE,
          progressUpdates,
          enteredAt: admittedDate ?? undefined,
          locationEpisodeId: locationEpisode.id,
        },
        {
          onSuccess: handleSuccess,
          onError: () => addToast({ text: 'Something went wrong. Please try again.' }),
        }
      );
    } else {
      mutate(
        {
          ...getSerializedNoteValues(),
          type: ActivityType.PROGRESS_UPDATE,
          questions: questionsRef.current!.getAnswers(),
          enteredAt: admittedDate ?? undefined,
          locationEpisodeId: locationEpisode.id,
        },
        {
          onSuccess: handleSuccess,
          onError: () => addToast({ text: 'Something went wrong. Please try again.' }),
        }
      );
    }
  };

  const latestStatus = locationEpisode.latestProgressUpdate?.data?.questions?.find((q) => q.kind === 'status')?.answer
    .text;

  const submitDisabled =
    isUploading || !allQuestionsAnswered || (locationEpisode?.currentRehabState.queue && !admittedDate);

  const questions: QuestionType[] = useMemo(() => {
    const baseQuestions =
      isSharedFlow && currentTemplate ? currentTemplate.questions : locationEpisode.progressTemplate.questions;

    if (!isSharedFlow || !currentTemplate) return baseQuestions;

    const savedAnswers = collectedAnswers.get(currentTemplate.templateId);
    if (!savedAnswers?.length) return baseQuestions;

    return savedAnswers;
  }, [isSharedFlow, currentTemplate, locationEpisode.progressTemplate.questions, collectedAnswers]);

  return (
    <StandardModal title={title} onCancel={cancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormContainer>
        {isSharedFlow && !isSingleTemplate && currentTemplate && (
          <StepIndicator>
            Record {currentStep + 1} of {totalSteps}: {currentTemplate.displayNames.join(', ')}
          </StepIndicator>
        )}
        {locationEpisode?.dischargeOverdue && (
          <Alert style={{ marginBottom: 16 }}>
            <Alert.Text>
              Patient&apos;s projected {translate('discharge').toLowerCase()} date of&nbsp;
              <b>{simpleDate(locationEpisode.projectedDischargeReview?.date)}</b>
              &nbsp; has passed. Please provide details regarding any barriers to discharge.
            </Alert.Text>
          </Alert>
        )}
        {locationEpisode?.currentRehabState.queue && (
          <InputGroup title={`Actual ${translate('Admission')} Date`}>
            <DatePicker
              data-cy='actualAdmissionDate'
              placeholder='Select Date'
              maxDate={new Date()}
              selected={admittedDate}
              onChange={setAdmittedDate}
            />
          </InputGroup>
        )}
        <QuestionList
          ref={questionsRef}
          questions={questions}
          setAllQuestionsAnswered={setAllQuestionsAnswered}
          latestStatus={latestStatus}
          locationEpisode={locationEpisode}
          episode={episode}
          noteValues={note}
          setNoteValues={setNote}
          resetNoteValues={resetNote}
          setIsUploading={setIsUploading}
        />
      </FormContainer>
      <Actions>
        {isSharedFlow && currentStep > 0 ? (
          <Button variant='ghost' onClick={handleBack}>
            Back
          </Button>
        ) : (
          <Button variant='ghost' onClick={cancel}>
            Cancel
          </Button>
        )}
        {isLastStep ? (
          <Button onClick={submit} disabled={submitDisabled} loading={isPending}>
            {isSharedFlow && !isSingleTemplate ? 'Submit All' : 'Submit'}
          </Button>
        ) : (
          <Button onClick={handleNext} disabled={!allQuestionsAnswered}>
            Next
          </Button>
        )}
      </Actions>
    </StandardModal>
  );
}

const FormContainer = styled.div`
  padding-bottom: 20px;
`;

const StepIndicator = styled.div`
  text-align: center;
  font-weight: 600;
  color: var(--primary-blue);
  margin-bottom: 16px;
  padding: 8px;
  background: var(--black-05);
  border-radius: 4px;
`;
